import { useEffect, useMemo, useRef, useState } from 'react';

import { type QueryClient, skipToken, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';

import { useMigrationReportSuccess } from '~/pages/AskRichardUnified/migrationOpenTracking';
import type { LensDocumentData, LensState } from '~/pages/LeLens/document/types';

import { reportingLeLensDataPath } from '~/consts/reportingLeLensApi';

import useCurrentUser from '~/hooks/useCurrentUser';

import { loadLensDocumentStream } from '~/utils/loadLensDocumentStream';
import { requestWithTimeout } from '~/utils/requestWithTimeout';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';

const DATA_TIMEOUT_MS = 90_000;

type Held = { period?: string; scope?: string; reader?: string; data: LensDocumentData };

/** Canonical reader identity. Array ordering does not change an OR selection. */
export function optionScopeKey(value: unknown): string {
  const canonical = (entry: unknown): unknown => {
    if (Array.isArray(entry))
      return entry.map(canonical).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
    if (entry && typeof entry === 'object')
      return Object.fromEntries(
        Object.entries(entry)
          .filter(([, value]) => value !== undefined)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([key, value]) => [key, canonical(value)]),
      );
    return entry;
  };
  return JSON.stringify(canonical(value));
}

export function canReuseFilterOptions(
  prior:
    | { scope?: string; data: { options?: LensDocumentData['options']; optionMeta?: LensDocumentData['optionMeta'] } }
    | undefined,
  state: LensState,
  scope = optionScopeKey(state),
): boolean {
  return Boolean(
    prior &&
    prior.scope === scope &&
    prior.data.optionMeta &&
    Object.values(prior.data.optionMeta).every((meta) => meta.status !== 'error' && !meta.stale) &&
    Object.keys(state.filters ?? {}).every((column) => prior.data.options?.[column] !== undefined),
  );
}

export function runRefreshOnce<T>(
  pending: { current: boolean },
  active: boolean,
  onStart: () => void,
  refetch: () => Promise<T>,
): Promise<T | undefined> {
  if (pending.current || active) return Promise.resolve(undefined);
  pending.current = true;
  try {
    onStart();
    return refetch().finally(() => {
      pending.current = false;
    });
  } catch (error) {
    pending.current = false;
    return Promise.reject(error);
  }
}

export const lensHeldKey = (lensId: string, lensVersion: number | undefined, reader?: string) =>
  reader
    ? (['lens-held', lensId, lensVersion, 'reader', reader] as const)
    : (['lens-held', lensId, lensVersion] as const);

export function carryLensData(client: QueryClient, lensId: string, from: number, to: number) {
  const prefix = [...reportingLeLensQueryKeys.detail(lensId), 'data', 'document'];
  const versionAt = prefix.length + 1;
  for (const query of client.getQueryCache().findAll({ queryKey: prefix })) {
    if (query.queryKey[versionAt] !== from || query.state.status !== 'success') continue;
    const target = [...query.queryKey.slice(0, versionAt), to, ...query.queryKey.slice(versionAt + 1)];
    client.setQueryData(target, { ...(query.state.data as LensDocumentData), lensVersion: to });
  }
  for (const query of client.getQueryCache().findAll({ queryKey: lensHeldKey(lensId, from) })) {
    client.setQueryData([...lensHeldKey(lensId, to), ...query.queryKey.slice(3)], query.state.data);
  }
}

export function withLentBlock(data: LensDocumentData, from: string, to: string): LensDocumentData {
  const source = data.blocks[from];
  if (!source) return data;
  const target = data.blocks[to];
  const planned = (block: typeof source | undefined) => block?.queries ?? (block?.query ? [block.query] : []);
  return {
    ...data,
    blocks: {
      ...data.blocks,
      [to]: { ...(target ?? source), queries: [...new Set([...planned(target), ...planned(source)])] },
    },
  };
}

export function lendBlockData(client: QueryClient, lensId: string, version: number, from: string, to: string) {
  const prefix = [...reportingLeLensQueryKeys.detail(lensId), 'data', 'document'];
  const versionAt = prefix.length + 1;
  const lend = (data: LensDocumentData) => withLentBlock(data, from, to);
  for (const query of client.getQueryCache().findAll({ queryKey: prefix })) {
    if (query.queryKey[versionAt] === version && query.state.status === 'success')
      client.setQueryData(query.queryKey, lend(query.state.data as LensDocumentData));
  }
  for (const query of client.getQueryCache().findAll({ queryKey: lensHeldKey(lensId, version) })) {
    const held = query.state.data as Held | undefined;
    if (held) client.setQueryData<Held>(query.queryKey, { ...held, data: lend(held.data) });
  }
}

export function withoutParams(state: LensState, names: Array<string>): LensState {
  if (!names.length || !state.params) return state;
  const { params, ...rest } = state;
  const kept = Object.entries(params).filter(([name]) => !names.includes(name));
  return kept.length ? { ...rest, params: Object.fromEntries(kept) } : rest;
}

function chosenLocally(data: LensDocumentData | undefined, state: LensState): LensDocumentData | undefined {
  if (!data || !Object.values(data.blocks).some((block) => block.fold)) return data;
  const params = {
    ...data.params,
    ...Object.fromEntries(
      (data.folded ?? []).flatMap((name) => (state.params?.[name] !== undefined ? [[name, state.params[name]]] : [])),
    ),
  };
  const queries = { ...data.queries };
  for (const block of Object.values(data.blocks)) {
    if (!block.fold) continue;
    const chosen = String(params[block.fold.param] ?? '');
    for (const id of block.queries ?? (block.query ? [block.query] : [])) {
      const envelope = queries[id];
      if (envelope)
        queries[id] = {
          ...envelope,
          columns: envelope.columns.filter(
            (column) =>
              column.role !== 'measure' || !block.fold!.measures.includes(column.name) || column.name === chosen,
          ),
        };
    }
  }
  return { ...data, params, queries };
}

function blockQueryKeys(data: LensDocumentData, blockId: string): Array<string> {
  const block = data.blocks[blockId];
  return (block?.queries ?? (block?.query ? [block.query] : [])).flatMap((id) => {
    const key = data.queries[id]?.key ?? data.keys?.[id];
    return key ? [key] : [];
  });
}

function carried(next: LensDocumentData, prior: LensDocumentData | undefined, reuseOptions: boolean): LensDocumentData {
  if (!prior) return next;
  const wanted = [
    ...Object.values(next.blocks).flatMap((block) => block.queries ?? (block.query ? [block.query] : [])),
    ...(next.lead ?? []),
  ];
  const held = Object.fromEntries(
    wanted.flatMap((id) =>
      prior.queries[id]?.key && prior.queries[id].key === next.keys?.[id] ? [[id, prior.queries[id]]] : [],
    ),
  );
  return {
    ...next,
    queries: { ...held, ...next.queries },
    options: reuseOptions || !next.optionMeta ? prior.options : next.options,
    optionMeta: reuseOptions || !next.optionMeta ? prior.optionMeta : next.optionMeta,
  };
}

export function carryHeldData(client: QueryClient, lensId: string, from: number, to: number) {
  for (const query of client.getQueryCache().findAll({ queryKey: lensHeldKey(lensId, from) })) {
    const held = query.state.data as Held | undefined;
    if (held)
      client.setQueryData<Held>([...lensHeldKey(lensId, to), ...query.queryKey.slice(3)], {
        ...held,
        data: { ...held.data, folded: undefined },
      });
  }
}

export function useGetReportingLensDocumentData(
  lensId: string | undefined,
  lensVersion: number | undefined,
  boardState: LensState,
  shownTabs?: Array<string>,
) {
  const client = useQueryClient();
  const { user } = useCurrentUser();
  const reader = user?.email?.trim().toLowerCase();
  const generation = useRef(0);
  const fresh = useRef<boolean | string>(false);
  const refreshPending = useRef(false);
  const heldKey = lensHeldKey(lensId ?? '', lensVersion, reader);
  const { data: storedHeld } = useQuery<Held>({ queryKey: heldKey, queryFn: skipToken, staleTime: Infinity });
  const held = storedHeld;
  const requested = withoutParams(boardState, held?.data.folded ?? []);
  const scope = optionScopeKey([reader, lensId, lensVersion, requested, shownTabs]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const [settled, setSettled] = useState({ scope, state: requested });
  useEffect(() => {
    const timer = setTimeout(() => setSettled({ scope, state: requested }), 300);
    return () => clearTimeout(timer);
  }, [scope]);
  const state = settled.state;
  const identity = JSON.stringify([lensId, lensVersion, state]);
  const shown = useRef(shownTabs);
  shown.current = shownTabs;
  const [partial, setPartial] = useState<{ identity: string; data: LensDocumentData }>();
  const query = useQuery({
    queryKey: [...reportingLeLensQueryKeys.detail(lensId ?? ''), 'data', 'document', state, lensVersion, settled.scope],
    queryFn: async ({ signal }): Promise<LensDocumentData & { loadedTabs?: Array<string>; reader?: string }> => {
      const own = ++generation.current;
      const tabs = shown.current;
      const refresh = fresh.current;
      fresh.current = false;
      const storedPrior = refresh === true ? undefined : client.getQueryData<Held>(heldKey);
      const prior = storedPrior;
      const reuseOptions = canReuseFilterOptions(prior, state, settled.scope);
      const stale = typeof refresh === 'string' && prior ? blockQueryKeys(prior.data, refresh) : [];
      const known = prior
        ? Object.values(prior.data.queries).flatMap((envelope) =>
            envelope.status !== 'error' && envelope.key && !stale.includes(envelope.key) ? [envelope.key] : [],
          )
        : [];
      setPartial(undefined);
      const streamed = await loadLensDocumentStream(
        resolveReportingLeLensUrl(reportingLeLensDataPath(lensId ?? '')),
        {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            state,
            lensVersion,
            stream: true,
            ...(tabs ? { tabs } : {}),
            ...(known.length ? { known } : {}),
            ...(reuseOptions ? { reuseOptions } : {}),
            ...(refresh ? { refresh: true } : {}),
          }),
          signal,
        },
        (data) => {
          if (!signal.aborted && own === generation.current && settled.scope === currentScope.current)
            setPartial({ identity, data: carried(data, prior?.data, reuseOptions) });
        },
        DATA_TIMEOUT_MS,
        'This Lens’s numbers are taking longer than expected. Nothing was changed, so try again.',
      );
      if (signal.aborted || settled.scope !== currentScope.current)
        throw new DOMException('Superseded choices request', 'AbortError');
      const data = carried(streamed, prior?.data, reuseOptions);
      for (const [column, meta] of Object.entries(data.optionMeta ?? {})) {
        if (
          (meta.status === 'error' || meta.unsupported.includes('unlinked_options_unavailable')) &&
          prior?.data.options?.[column]
        )
          data.options = { ...data.options, [column]: prior.data.options[column] };
      }
      client.setQueryData<Held>(heldKey, { period: state.period, scope: settled.scope, reader, data });
      return { ...data, reader, ...(tabs ? { loadedTabs: tabs } : {}) };
    },
    enabled: Boolean(lensId),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previous) =>
      previous && previous.lensId === lensId && previous.lensVersion === lensVersion && previous.reader === reader
        ? previous
        : undefined,
  });
  useMigrationReportSuccess(
    'lens',
    lensId,
    query.isSuccess &&
      !query.isFetching &&
      !query.isPlaceholderData &&
      Boolean(query.data && Object.keys(query.data.queries).length) &&
      Object.values(query.data?.queries ?? {}).every((envelope) => envelope.status === 'ready'),
  );
  const loaded = query.data?.loadedTabs;
  const missing =
    scope === settled.scope && shownTabs && loaded && !query.isPlaceholderData
      ? shownTabs.filter((tab) => !loaded.includes(tab))
      : [];
  const extra = useQueries({
    queries: missing.map((tab) => ({
      queryKey: [
        ...reportingLeLensQueryKeys.detail(lensId ?? ''),
        'data',
        'document',
        state,
        lensVersion,
        'tab',
        tab,
        reader,
      ],
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        requestWithTimeout<LensDocumentData>(
          resolveReportingLeLensUrl(reportingLeLensDataPath(lensId ?? '')),
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ state, lensVersion, tabs: [tab], tabsOnly: true }),
            signal,
          },
          'This tab’s numbers are taking longer than expected. Nothing was changed, so try again.',
          DATA_TIMEOUT_MS,
        ),
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: false,
    })),
  });
  const streamed = partial?.identity === identity && (query.isFetching || query.isError) ? partial.data : undefined;
  const shownState = useRef(state);
  if (streamed || (!query.isPlaceholderData && !query.isFetching)) shownState.current = state;
  const waiting =
    optionScopeKey(requested) !== optionScopeKey(withoutParams(shownState.current, held?.data.folded ?? []));
  const base = streamed ?? query.data ?? held?.data;
  const merged =
    base && extra.some((entry) => entry.data)
      ? extra.reduce<LensDocumentData>(
          (data, entry) =>
            entry.data
              ? {
                  ...data,
                  blocks: { ...data.blocks, ...entry.data.blocks },
                  queries: { ...data.queries, ...entry.data.queries },
                  problems: [...data.problems, ...entry.data.problems],
                }
              : data,
          base,
        )
      : base;
  const localParams = JSON.stringify(boardState.params ?? {});
  const current = scope === settled.scope && !query.isFetching && !query.isPlaceholderData && !query.isError;
  const data = useMemo(() => {
    const chosen = chosenLocally(merged, boardState);
    if (!chosen || current) return chosen;
    return {
      ...chosen,
      optionMeta: Object.fromEntries(
        Object.entries(chosen.optionMeta ?? {}).map(([column, meta]) => [
          column,
          {
            ...meta,
            ...(query.isError ? { status: 'error' as const } : {}),
            stale: true,
            selected: meta.selected.map(({ value }) => ({ value })),
          },
        ]),
      ),
    };
  }, [merged, localParams, current, query.isError]);
  const settling = query.isFetching && query.data !== undefined && query.data.lensVersion !== lensVersion;
  const refresh = (blockId?: string) =>
    runRefreshOnce(
      refreshPending,
      query.isFetching,
      () => {
        fresh.current = blockId ?? true;
      },
      () => query.refetch(),
    );
  return {
    ...query,
    data,
    settling,
    waiting,
    refresh,
    isPlaceholderData: streamed || held ? false : query.isPlaceholderData,
  };
}
